/**
 * Descriptive statistics from a grouped frequency table.
 *
 * Every value in a class is treated as sitting at the class midpoint x, so
 * mean = Σfx ÷ n and the sample variance is s² = (Σfx² − (Σfx)² ÷ n) ÷ (n − 1).
 * Medians and quartiles interpolate inside their class (the cumulative-frequency polygon):
 *
 *   value = L + ((p × n − CF) ÷ f) × w
 *
 * with L the lower class boundary, CF the cumulative frequency before the class, f its
 * frequency and w its width. The mode uses the standard modal-class formula
 *
 *   mode = L + (Δ1 ÷ (Δ1 + Δ2)) × w,  Δ1 = f₁ − f₀,  Δ2 = f₁ − f₂,
 *
 * where f₀ and f₂ are the frequencies of the classes either side. For classes of unequal
 * width the modal class and Δ are taken on frequency density (f ÷ w), the usual
 * generalisation. References: Spiegel & Stephens, *Schaum's Outline of Statistics*, and
 * any introductory statistics text's chapter on grouped data.
 */

import { parseNumberInput } from "@/lib/parse-number";

const LINE_BREAK = /\r?\n/;
/** A row: everything up to a final whole or decimal-point number, the frequency. */
const ROW = /^(.*?)[\s,;:|]+(\d+(?:\.\d+)?)$/;
/** A class written as a range: 10-20, 10–20, 10 to 20, −5 to −1. */
const RANGE = /^(-?[\d.,]+)\s*(?:[-–—]|to)\s*(-?[\d.,]+)$/i;
const TOKEN_SPLIT = /[\s,;:|]+/;
const MINUS = /−/g;

export interface ClassRow {
  lower: number;
  upper: number;
  freq: number;
}

export interface ParsedClasses {
  rows: ClassRow[];
  /** 1-based line numbers that could not be read. */
  skipped: number[];
}

function classLimits(text: string): [number, number] | null {
  const clean = text.replace(MINUS, "-").trim();
  const range = RANGE.exec(clean);
  const parts = range
    ? [range[1], range[2]]
    : clean.split(TOKEN_SPLIT).filter((part) => part !== "");
  if (parts.length !== 2) {
    return null;
  }
  const lower = parseNumberInput(parts[0]);
  const upper = parseNumberInput(parts[1]);
  if (lower === null || upper === null || upper <= lower) {
    return null;
  }
  return [lower, upper];
}

/** One class per line: `10-20, 5`, `10 to 20: 5` or `10 20 5`. Blank lines are ignored. */
export function parseClassLines(text: string): ParsedClasses {
  const rows: ClassRow[] = [];
  const skipped: number[] = [];
  const lines = text.split(LINE_BREAK);
  for (const [index, line] of lines.entries()) {
    if (line.trim() === "") {
      continue;
    }
    const match = ROW.exec(line.trim());
    const limits = match ? classLimits(match[1]) : null;
    if (match && limits) {
      rows.push({ lower: limits[0], upper: limits[1], freq: Number(match[2]) });
    } else {
      skipped.push(index + 1);
    }
  }
  rows.sort((a, b) => a.lower - b.lower);
  return { rows, skipped };
}

export interface GroupedClass extends ClassRow {
  /** Boundaries: the limits themselves, or widened by half the gap between classes. */
  lowerBoundary: number;
  upperBoundary: number;
  width: number;
  midpoint: number;
  /** Cumulative frequency up to and including this class. */
  cumulative: number;
  fx: number;
  fx2: number;
}

export type BoundaryMode = "detect" | "asWritten";

export interface QuantileStep {
  p: number;
  /** The target position p × n in the cumulative frequency. */
  target: number;
  classIndex: number;
  lowerBoundary: number;
  cumulativeBefore: number;
  freq: number;
  width: number;
  value: number;
}

export interface ModeStep {
  classIndex: number;
  lowerBoundary: number;
  width: number;
  /** f₁, f₀ and f₂ on frequency density when widths differ, frequency otherwise. */
  f1: number;
  f0: number;
  f2: number;
  delta1: number;
  delta2: number;
  value: number;
}

export interface GroupedReport {
  classes: GroupedClass[];
  n: number;
  sumFx: number;
  sumFx2: number;
  mean: number;
  varianceSample: number | null;
  variancePopulation: number;
  sdSample: number | null;
  sdPopulation: number;
  median: QuantileStep;
  q1: QuantileStep;
  q3: QuantileStep;
  modes: ModeStep[];
  equalWidths: boolean;
  /** Half the gap added to each side of a class when limits were converted. */
  boundaryAdjustment: number;
}

export type GroupedProblem = "overlap" | "noData";

const EPSILON = 1e-9;

/** All gaps between consecutive classes equal and positive: half of it, else 0. */
function boundaryAdjustment(rows: readonly ClassRow[]): number {
  if (rows.length < 2) {
    return 0;
  }
  const gaps = rows.slice(1).map((row, i) => row.lower - rows[i].upper);
  const first = gaps[0];
  const regular = gaps.every((gap) => Math.abs(gap - first) < EPSILON);
  return regular && first > EPSILON ? first / 2 : 0;
}

function hasOverlap(rows: readonly ClassRow[]): boolean {
  return rows.some(
    (row, i) => i > 0 && row.lower < rows[i - 1].upper - EPSILON
  );
}

function quantileStep(
  classes: readonly GroupedClass[],
  n: number,
  p: number
): QuantileStep {
  const target = p * n;
  let index = classes.findIndex(
    (each) => each.freq > 0 && each.cumulative >= target - EPSILON
  );
  if (index === -1) {
    index = classes.length - 1;
  }
  const chosen = classes[index];
  const before = chosen.cumulative - chosen.freq;
  return {
    p,
    target,
    classIndex: index,
    lowerBoundary: chosen.lowerBoundary,
    cumulativeBefore: before,
    freq: chosen.freq,
    width: chosen.width,
    value:
      chosen.lowerBoundary + ((target - before) / chosen.freq) * chosen.width,
  };
}

function modeSteps(
  classes: readonly GroupedClass[],
  equal: boolean
): ModeStep[] {
  const measure = (each: GroupedClass | undefined): number => {
    if (!each) {
      return 0;
    }
    return equal ? each.freq : each.freq / each.width;
  };
  const top = Math.max(...classes.map((each) => measure(each)));
  const steps: ModeStep[] = [];
  for (const [index, each] of classes.entries()) {
    if (Math.abs(measure(each) - top) > EPSILON) {
      continue;
    }
    const f1 = measure(each);
    const f0 = measure(classes[index - 1]);
    const f2 = measure(classes[index + 1]);
    const delta1 = f1 - f0;
    const delta2 = f1 - f2;
    const total = delta1 + delta2;
    steps.push({
      classIndex: index,
      lowerBoundary: each.lowerBoundary,
      width: each.width,
      f1,
      f0,
      f2,
      delta1,
      delta2,
      value:
        total === 0
          ? (each.lowerBoundary + each.upperBoundary) / 2
          : each.lowerBoundary + (delta1 / total) * each.width,
    });
  }
  return steps;
}

export function groupedReport(
  rows: readonly ClassRow[],
  mode: BoundaryMode = "detect"
): GroupedReport | GroupedProblem {
  const usable = rows;
  const n = usable.reduce((total, row) => total + row.freq, 0);
  if (usable.length === 0 || n <= 0) {
    return "noData";
  }
  if (hasOverlap(usable)) {
    return "overlap";
  }
  const adjust = mode === "detect" ? boundaryAdjustment(usable) : 0;
  let cumulative = 0;
  const classes: GroupedClass[] = usable.map((row) => {
    cumulative += row.freq;
    const midpoint = (row.lower + row.upper) / 2;
    return {
      ...row,
      lowerBoundary: row.lower - adjust,
      upperBoundary: row.upper + adjust,
      width: row.upper - row.lower + 2 * adjust,
      midpoint,
      cumulative,
      fx: row.freq * midpoint,
      fx2: row.freq * midpoint * midpoint,
    };
  });
  const sumFx = classes.reduce((total, each) => total + each.fx, 0);
  const sumFx2 = classes.reduce((total, each) => total + each.fx2, 0);
  const mean = sumFx / n;
  const sumSquares = classes.reduce(
    (total, each) => total + each.freq * (each.midpoint - mean) ** 2,
    0
  );
  const varianceSample = n > 1 ? sumSquares / (n - 1) : null;
  const variancePopulation = sumSquares / n;
  const equalWidths = classes.every(
    (each) => Math.abs(each.width - classes[0].width) < EPSILON
  );
  return {
    classes,
    n,
    sumFx,
    sumFx2,
    mean,
    varianceSample,
    variancePopulation,
    sdSample: varianceSample === null ? null : Math.sqrt(varianceSample),
    sdPopulation: Math.sqrt(variancePopulation),
    median: quantileStep(classes, n, 0.5),
    q1: quantileStep(classes, n, 0.25),
    q3: quantileStep(classes, n, 0.75),
    modes: modeSteps(classes, equalWidths),
    equalWidths,
    boundaryAdjustment: adjust,
  };
}

export function isGroupedReport(
  value: GroupedReport | GroupedProblem
): value is GroupedReport {
  return typeof value !== "string";
}
